/**
 * Sprint 5 — Suivi personnalisé (Personal Follow-Up) — Kaizen System.
 * CRM léger : prochain pas + owner + échéance + timeline. Lié à recouvrement & migration.
 */
import { useEffect, useState } from "react";
import { api } from "../lib/api";
import { toast } from "sonner";
import {
  ClipboardList, Loader2, RefreshCw, Plus, CheckCircle2, Clock, AlertTriangle,
  PhoneOff, Sparkles, User, X, CalendarClock, StickyNote, Flame,
} from "lucide-react";
import { PageHeader } from "../components/ui/kaizen";

const PRIO_COLORS = { critical: "bg-red-600/30 text-red-200", high: "bg-rose-500/20 text-rose-300", medium: "bg-amber-500/20 text-amber-300", low: "bg-zinc-700 text-zinc-300" };
const DUE_COLORS = { overdue: "bg-rose-600/30 text-rose-200", due_today: "bg-amber-500/20 text-amber-300", upcoming: "bg-blue-500/20 text-blue-300", none: "bg-zinc-800 text-zinc-400" };

const Kpi = ({ label, value, icon: Icon, accent, testid }) => (
  <div className={`rounded-2xl border p-4 ${accent}`} data-testid={testid}>
    <div className="flex items-center justify-between"><span className="text-[11px] uppercase tracking-wide text-zinc-400">{label}</span><Icon className="w-4 h-4 opacity-70" /></div>
    <div className="mt-2 text-2xl font-bold">{value}</div>
  </div>
);

export default function FollowUpModule() {
  const [loading, setLoading] = useState(true);
  const [dash, setDash] = useState(null);
  const [meta, setMeta] = useState({ categories: [], statuses: [], priorities: [] });
  const [tab, setTab] = useState("now");
  const [rows, setRows] = useState([]);
  const [detail, setDetail] = useState(null);
  const [pipeline, setPipeline] = useState(null);
  const [notifs, setNotifs] = useState({ notifications: [], unread: 0, week_revenue: 0 });
  const [showCreate, setShowCreate] = useState(false);
  const [form, setForm] = useState({ client_name: "", client_email: "", follow_up_type: "general_checkin", priority: "medium", due_at: "", next_step_summary: "", reactivation_flag: false });

  const loadDash = async () => {
    setLoading(true);
    try {
      const [d, m, n] = await Promise.all([
        api.get("/followups/dashboard"),
        api.get("/followups/meta"),
        api.get("/followups/reactivation/notifications"),
      ]);
      setDash(d.data); setMeta(m.data); setNotifs(n.data);
    } catch (e) { toast.error("Erreur de chargement des suivis"); }
    setLoading(false);
  };
  useEffect(() => { loadDash(); }, []);

  const dismissNotifs = async () => {
    await api.post("/followups/reactivation/notifications/mark-read");
    setNotifs({ ...notifs, unread: 0 });
  };

  const loadTab = async (t) => {
    setTab(t);
    const map = { overdue: "due_state=overdue", no_response: "status=no_response", reactivation: "reactivation=true", mine: "" };
    if (t === "now") return;
    if (t === "pipeline") {
      try { setPipeline((await api.get("/followups/reactivation/pipeline")).data); } catch (e) { toast.error("Erreur pipeline"); }
      return;
    }
    try {
      const r = await api.get(`/followups?${map[t] || ""}`);
      setRows(r.data.follow_ups);
    } catch (e) { toast.error("Erreur de chargement"); }
  };

  const setOutcome = async (id, outcome) => {
    let revenue = null;
    if (outcome === "enrolled") {
      const v = window.prompt("Revenu généré par cette réinscription ($) :", "0");
      if (v === null) return;
      revenue = parseFloat(v) || 0;
    }
    await api.post(`/followups/${id}/reactivation-outcome`, { outcome, revenue_amount: revenue });
    toast.success(outcome === "enrolled" ? "Réinscription enregistrée 🎉" : "Issue enregistrée");
    if (detail) openDetail(id);
    loadDash();
    if (tab === "pipeline") setPipeline((await api.get("/followups/reactivation/pipeline")).data);
  };

  const openDetail = async (id) => {
    try { setDetail((await api.get(`/followups/${id}`)).data); } catch (e) { toast.error("Introuvable"); }
  };

  const createFollowUp = async () => {
    if (!form.client_name.trim() || !form.next_step_summary.trim()) { toast.error("Nom + prochain pas obligatoires"); return; }
    try {
      await api.post("/followups", form);
      toast.success("Suivi créé ✅");
      setShowCreate(false);
      setForm({ client_name: "", client_email: "", follow_up_type: "general_checkin", priority: "medium", due_at: "", next_step_summary: "", reactivation_flag: false });
      loadDash(); if (tab !== "now") loadTab(tab);
    } catch (e) { toast.error(e?.response?.data?.detail || "Erreur"); }
  };

  const complete = async (id) => { await api.post(`/followups/${id}/complete`); toast.success("Suivi complété"); loadDash(); if (tab !== "now") loadTab(tab); setDetail(null); };
  const snooze = async (id) => { const d = window.prompt("Reporter au (AAAA-MM-JJ) :"); if (!d) return; await api.post(`/followups/${id}/snooze`, { due_at: d }); toast.success("Reporté"); loadDash(); if (tab !== "now") loadTab(tab); if (detail) openDetail(id); };
  const setStatus = async (id, status) => { await api.patch(`/followups/${id}`, { status }); toast.success("Statut mis à jour"); loadDash(); if (tab !== "now") loadTab(tab); if (detail) openDetail(id); };
  const addNote = async (id) => { const t = window.prompt("Note :"); if (!t) return; await api.post(`/followups/${id}/note`, { note_text: t, set_last_contact: true }); toast.success("Note ajoutée"); if (detail) openDetail(id); };

  if (loading) return <div className="flex items-center justify-center h-64"><Loader2 className="w-6 h-6 animate-spin text-amber-400" /></div>;
  const k = dash?.kpis || {};
  const list = tab === "now" ? (dash?.action_now || []) : rows;

  return (
    <div className="space-y-6 text-zinc-100 p-6 md:p-8" data-testid="followup-module">
      <PageHeader
        title="Suivi personnalisé"
        icon={ClipboardList}
        breadcrumbs={[{ label: "CRM & relations" }, { label: "Suivi personnalisé" }]}
        subtitle="Prochain pas clair · propriétaire · échéance · timeline. Lié au recouvrement & à la migration."
        actions={
          <>
            <button onClick={() => setShowCreate(true)} className="px-3 py-2 rounded-lg bg-amber-600/30 hover:bg-amber-600/50 text-amber-100 text-sm flex items-center gap-1" data-testid="followup-create-btn"><Plus className="w-4 h-4" /> Nouveau suivi</button>
            <button onClick={loadDash} className="px-3 py-2 rounded-lg bg-zinc-800 hover:bg-zinc-700 text-sm flex items-center gap-1" data-testid="followup-refresh"><RefreshCw className="w-4 h-4" /> Actualiser</button>
          </>
        }
      />

      {notifs.unread > 0 && (
        <div className="rounded-2xl border border-emerald-500/40 bg-emerald-500/10 p-4 flex items-center justify-between gap-3 flex-wrap" data-testid="reactivation-notif-banner">
          <div className="flex items-center gap-3">
            <span className="text-2xl">🎉</span>
            <div>
              <div className="text-sm font-semibold text-emerald-200">{notifs.unread} réinscription(s) récente(s) !</div>
              <div className="text-xs text-emerald-300/80">
                {notifs.notifications.slice(0, 3).map((n) => n.client_name).join(", ")}
                {notifs.week_revenue > 0 && ` · ${notifs.week_revenue.toLocaleString("fr-CA", { style: "currency", currency: "CAD" })} récupérés cette semaine`}
              </div>
            </div>
          </div>
          <button onClick={dismissNotifs} className="px-3 py-1.5 rounded-lg bg-emerald-600/30 hover:bg-emerald-600/50 text-emerald-100 text-xs" data-testid="dismiss-notif">Marquer comme vu</button>
        </div>
      )}

      <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-3">
        <Kpi label="Ouverts" value={k.open || 0} icon={ClipboardList} accent="border-zinc-700 bg-zinc-900" testid="kpi-open" />
        <Kpi label="Aujourd'hui" value={k.due_today || 0} icon={Clock} accent="border-amber-500/30 bg-amber-500/5" testid="kpi-today" />
        <Kpi label="En retard" value={k.overdue || 0} icon={AlertTriangle} accent="border-rose-500/30 bg-rose-500/5" testid="kpi-overdue" />
        <Kpi label="Sans réponse" value={k.no_response || 0} icon={PhoneOff} accent="border-orange-500/30 bg-orange-500/5" testid="kpi-noresp" />
        <Kpi label="Réactivation" value={k.reactivation || 0} icon={Sparkles} accent="border-blue-500/30 bg-blue-500/5" testid="kpi-react" />
        <Kpi label="Mes suivis" value={k.mine || 0} icon={User} accent="border-emerald-500/30 bg-emerald-500/5" testid="kpi-mine" />
      </div>

      <div className="flex gap-2 border-b border-zinc-800 flex-wrap">
        {[["now", "Action requise", Flame], ["mine", "Mes suivis", User], ["overdue", "En retard", AlertTriangle], ["no_response", "Sans réponse", PhoneOff], ["reactivation", "Réactivation", Sparkles], ["pipeline", "Pipeline réactivation", Sparkles]].map(([id, label, Icon]) => (
          <button key={id} onClick={() => loadTab(id)} data-testid={`followup-tab-${id}`}
            className={`px-3 py-2 text-sm flex items-center gap-1 border-b-2 -mb-px ${tab === id ? "border-amber-400 text-amber-300" : "border-transparent text-zinc-400 hover:text-zinc-200"}`}>
            <Icon className="w-4 h-4" /> {label}
          </button>
        ))}
      </div>

      {/* Pipeline réactivation (entonnoir) */}
      {tab === "pipeline" && pipeline && (
        <div className="space-y-4" data-testid="followup-pipeline">
          <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
            {pipeline.funnel.map((s, i) => (
              <div key={s.stage} className="rounded-2xl border border-zinc-800 bg-zinc-900 p-4 relative overflow-hidden" data-testid={`funnel-${i}`}>
                <div className="text-[11px] uppercase tracking-wide text-zinc-400">{i + 1}. {s.stage}</div>
                <div className="mt-2 text-3xl font-bold text-amber-300">{s.count}</div>
                {i > 0 && pipeline.funnel[i - 1].count > 0 && (
                  <div className="text-[11px] text-zinc-500 mt-1">{Math.round(100 * s.count / pipeline.funnel[i - 1].count)}% de l'étape précédente</div>
                )}
              </div>
            ))}
          </div>
          <div className="grid grid-cols-2 md:grid-cols-3 gap-3">
            <div className="rounded-2xl border border-emerald-500/30 bg-emerald-500/5 p-4"><div className="text-[11px] uppercase text-zinc-400">Revenu réactivé</div><div className="mt-1 text-2xl font-bold text-emerald-300">{pipeline.revenue.toLocaleString("fr-CA", { style: "currency", currency: "CAD" })}</div></div>
            <div className="rounded-2xl border border-blue-500/30 bg-blue-500/5 p-4"><div className="text-[11px] uppercase text-zinc-400">Taux de conversion</div><div className="mt-1 text-2xl font-bold text-blue-300">{pipeline.conversion_rate}%</div></div>
            <div className="rounded-2xl border border-orange-500/30 bg-orange-500/5 p-4"><div className="text-[11px] uppercase text-zinc-400">Sans réponse / en attente</div><div className="mt-1 text-2xl font-bold text-orange-300">{pipeline.no_response} / {pipeline.pending}</div></div>
          </div>
          {pipeline.enrolled_clients?.length > 0 && (
            <div className="rounded-xl border border-zinc-800 bg-zinc-900 p-4">
              <div className="text-xs uppercase text-zinc-500 mb-2">Réinscriptions 🎉</div>
              {pipeline.enrolled_clients.map((c, i) => (
                <div key={i} className="flex justify-between text-sm border-b border-zinc-800/50 py-1"><span>{c.client_name}</span><span className="text-emerald-300">{c.revenue_amount.toLocaleString("fr-CA", { style: "currency", currency: "CAD" })}</span></div>
              ))}
            </div>
          )}
          <p className="text-xs text-zinc-500">Astuce : sur une fiche de suivi réactivation, marque l'issue (« a répondu » / « réinscrit ») pour alimenter ce tableau.</p>
        </div>
      )}

      {tab !== "pipeline" && (
      <div className="overflow-x-auto rounded-xl border border-zinc-800" data-testid="followup-table">
        <table className="w-full text-sm">
          <thead className="bg-zinc-900 text-zinc-400 text-xs uppercase"><tr><th className="text-left p-3">Client</th><th className="text-left p-3">Catégorie</th><th className="text-left p-3">Prochain pas</th><th className="text-left p-3">Échéance</th><th className="text-left p-3">Priorité</th><th className="text-left p-3">Statut</th><th className="text-right p-3">Actions</th></tr></thead>
          <tbody>
            {list.map((f) => (
              <tr key={f.id} className="border-t border-zinc-800/60 hover:bg-zinc-900/40">
                <td className="p-3"><button onClick={() => openDetail(f.id)} className="font-medium text-amber-100 hover:underline">{f.client_name}</button><div className="text-[11px] text-zinc-500">{f.owner}</div></td>
                <td className="p-3 text-xs">{f.category_label}</td>
                <td className="p-3 text-xs text-zinc-300 max-w-xs truncate">{f.next_step_summary}</td>
                <td className="p-3"><span className={`px-2 py-0.5 rounded-full text-[11px] ${DUE_COLORS[f.due_state]}`}>{f.due_at || "—"}</span></td>
                <td className="p-3"><span className={`px-2 py-0.5 rounded-full text-[11px] ${PRIO_COLORS[f.priority]}`}>{f.priority}</span></td>
                <td className="p-3 text-xs">{f.status}</td>
                <td className="p-3"><div className="flex items-center gap-1 justify-end">
                  <button title="Note" onClick={() => addNote(f.id)} className="px-2 py-1 rounded bg-zinc-800 hover:bg-zinc-700 text-xs"><StickyNote className="w-3 h-3" /></button>
                  <button title="Reporter" onClick={() => snooze(f.id)} className="px-2 py-1 rounded bg-blue-600/30 hover:bg-blue-600/50 text-xs"><CalendarClock className="w-3 h-3" /></button>
                  <button title="Complété" onClick={() => complete(f.id)} className="px-2 py-1 rounded bg-emerald-600/30 hover:bg-emerald-600/50 text-xs" data-testid={`followup-complete-${f.id}`}><CheckCircle2 className="w-3 h-3" /></button>
                </div></td>
              </tr>
            ))}
            {list.length === 0 && <tr><td colSpan={7} className="p-8 text-center text-zinc-500">Aucun suivi ici 🎉</td></tr>}
          </tbody>
        </table>
      </div>
      )}

      {/* Charge équipe */}
      {tab === "now" && dash?.workload?.length > 0 && (
        <div><div className="text-xs uppercase text-zinc-500 mb-2">Charge par responsable</div>
          <div className="flex gap-2 flex-wrap">{dash.workload.map((w) => <span key={w.owner} className="px-3 py-1 rounded-lg bg-zinc-900 border border-zinc-800 text-xs">{w.owner} · <span className="text-amber-300 font-semibold">{w.count}</span></span>)}</div>
        </div>
      )}

      {/* Modal création */}
      {showCreate && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/60 p-4" onClick={() => setShowCreate(false)}>
          <div className="w-full max-w-lg bg-zinc-950 border border-zinc-800 rounded-2xl p-5 space-y-3" onClick={(e) => e.stopPropagation()} data-testid="followup-create-modal">
            <div className="flex items-center justify-between"><h3 className="text-lg font-bold">Nouveau suivi</h3><button onClick={() => setShowCreate(false)}><X className="w-5 h-5 text-zinc-400" /></button></div>
            <input value={form.client_name} onChange={(e) => setForm({ ...form, client_name: e.target.value })} placeholder="Nom du client *" className="w-full bg-zinc-900 border border-zinc-700 rounded px-3 py-2 text-sm" data-testid="form-client-name" />
            <input value={form.client_email} onChange={(e) => setForm({ ...form, client_email: e.target.value })} placeholder="Courriel (optionnel)" className="w-full bg-zinc-900 border border-zinc-700 rounded px-3 py-2 text-sm" />
            <div className="grid grid-cols-2 gap-3">
              <select value={form.follow_up_type} onChange={(e) => setForm({ ...form, follow_up_type: e.target.value })} className="bg-zinc-900 border border-zinc-700 rounded px-3 py-2 text-sm" data-testid="form-category">
                {meta.categories.map((c) => <option key={c.key} value={c.key}>{c.label}</option>)}
              </select>
              <select value={form.priority} onChange={(e) => setForm({ ...form, priority: e.target.value })} className="bg-zinc-900 border border-zinc-700 rounded px-3 py-2 text-sm">
                {meta.priorities.map((p) => <option key={p} value={p}>{p}</option>)}
              </select>
            </div>
            <input type="date" value={form.due_at} onChange={(e) => setForm({ ...form, due_at: e.target.value })} className="w-full bg-zinc-900 border border-zinc-700 rounded px-3 py-2 text-sm" data-testid="form-due" />
            <textarea value={form.next_step_summary} onChange={(e) => setForm({ ...form, next_step_summary: e.target.value })} placeholder="Prochain pas (obligatoire) *" className="w-full bg-zinc-900 border border-zinc-700 rounded px-3 py-2 text-sm h-20" data-testid="form-nextstep" />
            <label className="text-xs flex items-center gap-2 cursor-pointer"><input type="checkbox" checked={form.reactivation_flag} onChange={(e) => setForm({ ...form, reactivation_flag: e.target.checked })} /> Candidat réactivation</label>
            <button onClick={createFollowUp} className="w-full px-4 py-2 rounded-lg bg-amber-600/40 hover:bg-amber-600/60 text-amber-100 text-sm font-medium" data-testid="form-submit">Créer le suivi</button>
          </div>
        </div>
      )}

      {/* Drawer détail */}
      {detail && (
        <div className="fixed inset-0 z-50 flex justify-end bg-black/60" onClick={() => setDetail(null)}>
          <div className="w-full max-w-md bg-zinc-950 border-l border-zinc-800 h-full overflow-y-auto p-5" onClick={(e) => e.stopPropagation()} data-testid="followup-detail-drawer">
            <div className="flex items-center justify-between mb-4"><h3 className="text-lg font-bold">{detail.follow_up.client_name}</h3><button onClick={() => setDetail(null)}><X className="w-5 h-5 text-zinc-400" /></button></div>
            <div className="space-y-1 text-sm">
              <div>Catégorie : <span className="text-amber-300">{detail.follow_up.category_label}</span></div>
              <div>Prochain pas : {detail.follow_up.next_step_summary}</div>
              <div>Échéance : {detail.follow_up.due_at || "—"} · <span className={`px-2 py-0.5 rounded-full text-[11px] ${DUE_COLORS[detail.follow_up.due_state]}`}>{detail.follow_up.due_state}</span></div>
              <div>Statut : {detail.follow_up.status} · priorité {detail.follow_up.priority}</div>
              <div className="text-zinc-400 text-xs">{detail.follow_up.client_email || "pas de courriel"}</div>
              {detail.follow_up.linked_recovery_account_id && <div className="text-blue-300 text-xs">🔗 Lié à un dossier recouvrement</div>}
            </div>
            <div className="mt-4">
              <div className="text-xs uppercase text-zinc-500 mb-1">Changer le statut</div>
              <select onChange={(e) => { if (e.target.value) setStatus(detail.follow_up.id, e.target.value); }} className="w-full bg-zinc-900 border border-zinc-700 rounded px-2 py-1.5 text-sm" defaultValue="" data-testid="detail-status-select">
                <option value="">— Choisir —</option>
                {meta.statuses.map((s) => <option key={s} value={s}>{s}</option>)}
              </select>
            </div>
            <div className="mt-4">
              <div className="text-xs uppercase text-zinc-500 mb-1">Notes ({(detail.follow_up.notes || []).length})</div>
              {(detail.follow_up.notes || []).map((n, i) => <div key={i} className="text-[11px] text-zinc-400 border-b border-zinc-800/50 py-1">{n.text} <span className="text-zinc-600">— {n.by}</span></div>)}
            </div>
            <div className="mt-4">
              <div className="text-xs uppercase text-zinc-500 mb-1">Historique</div>
              {(detail.timeline || []).map((e) => <div key={e.event_id} className="text-[11px] text-zinc-400 py-1"><span className="text-amber-300">{e.event_type}</span> — {e.title}</div>)}
              {(detail.linked_recovery_events || []).map((e) => <div key={e.event_id} className="text-[11px] text-blue-300/70 py-1">🔗 {e.event_type} — {e.title}</div>)}
            </div>
            <div className="mt-5 flex gap-2">
              <button onClick={() => addNote(detail.follow_up.id)} className="flex-1 px-3 py-2 rounded bg-zinc-800 hover:bg-zinc-700 text-sm flex items-center justify-center gap-1"><StickyNote className="w-4 h-4" /> Note</button>
              <button onClick={() => complete(detail.follow_up.id)} className="flex-1 px-3 py-2 rounded bg-emerald-600/30 hover:bg-emerald-600/50 text-sm flex items-center justify-center gap-1"><CheckCircle2 className="w-4 h-4" /> Complété</button>
            </div>
            {(detail.follow_up.reactivation_flag || detail.follow_up.follow_up_type === "reactivation") && (
              <div className="mt-3">
                <div className="text-xs uppercase text-zinc-500 mb-1">Issue réactivation (pipeline)</div>
                <div className="flex gap-2">
                  <button onClick={() => setOutcome(detail.follow_up.id, "responded")} className="flex-1 px-2 py-1.5 rounded bg-blue-600/30 hover:bg-blue-600/50 text-xs" data-testid="outcome-responded">A répondu</button>
                  <button onClick={() => setOutcome(detail.follow_up.id, "enrolled")} className="flex-1 px-2 py-1.5 rounded bg-emerald-600/40 hover:bg-emerald-600/60 text-xs" data-testid="outcome-enrolled">Réinscrit 🎉</button>
                  <button onClick={() => setOutcome(detail.follow_up.id, "no_response")} className="flex-1 px-2 py-1.5 rounded bg-zinc-800 hover:bg-zinc-700 text-xs" data-testid="outcome-noresp">Sans réponse</button>
                </div>
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  );
}
